/**
 * FOV cone length in map units so the wedge stays a stable on-screen size
 * across map zoom levels (unlike a fixed map-unit clamp).
 */

export const FOV_CONE_TARGET_SCREEN_PX = 100;
export const FOV_CONE_RESOLUTION_DRIFT_ZOOM_LEVELS = 0.25;

export interface FovConeLengthInput {
  readonly resolution?: number | null;
  readonly extentWidth?: number | null;
  readonly viewWidthPx?: number | null;
  readonly targetScreenPx?: number;
}

export function computeFovConeLengthMapUnits(input: FovConeLengthInput): number {
  const targetPx = input.targetScreenPx ?? FOV_CONE_TARGET_SCREEN_PX;

  if (
    typeof input.resolution === 'number' &&
    Number.isFinite(input.resolution) &&
    input.resolution > 0
  ) {
    return input.resolution * targetPx;
  }

  if (
    typeof input.extentWidth === 'number' &&
    Number.isFinite(input.extentWidth) &&
    input.extentWidth > 0 &&
    typeof input.viewWidthPx === 'number' &&
    Number.isFinite(input.viewWidthPx) &&
    input.viewWidthPx > 0
  ) {
    return (input.extentWidth / input.viewWidthPx) * targetPx;
  }

  if (
    typeof input.extentWidth === 'number' &&
    Number.isFinite(input.extentWidth) &&
    input.extentWidth > 0
  ) {
    return input.extentWidth * 0.08;
  }

  return targetPx;
}

export function readMapViewResolution(mapView: {
  readonly resolution?: unknown;
  readonly extent?: { readonly width?: unknown } | null;
  readonly width?: unknown;
}): number | null {
  if (
    typeof mapView.resolution === 'number' &&
    Number.isFinite(mapView.resolution) &&
    mapView.resolution > 0
  ) {
    return mapView.resolution;
  }

  const extentWidth = mapView.extent?.width;
  const viewWidthPx = mapView.width;
  if (
    typeof extentWidth === 'number' &&
    Number.isFinite(extentWidth) &&
    extentWidth > 0 &&
    typeof viewWidthPx === 'number' &&
    Number.isFinite(viewWidthPx) &&
    viewWidthPx > 0
  ) {
    return extentWidth / viewWidthPx;
  }

  return null;
}

/**
 * True when map resolution has drifted enough that a retained cone length
 * would look wrong (roughly a fraction of a zoom level).
 */
export function hasMapResolutionDrifted(
  previousResolution: number | null | undefined,
  nextResolution: number | null | undefined,
  driftZoomLevels: number = FOV_CONE_RESOLUTION_DRIFT_ZOOM_LEVELS,
): boolean {
  if (
    previousResolution == null ||
    nextResolution == null ||
    !(previousResolution > 0) ||
    !(nextResolution > 0)
  ) {
    return false;
  }
  return (
    Math.abs(Math.log2(nextResolution / previousResolution)) > driftZoomLevels
  );
}
